import React, { useState, useEffect } from 'react';
import { BasicInfoStep } from './BasicInfoStep';
import { PhotosStep } from './PhotosStep';
import { BioStep } from './BioStep';
import { InterestsStep } from './InterestsStep';
import { PreferencesStep } from './PreferencesStep';
import { ProfileSetupData } from '../types';
import {
  WizardContainer,
  WizardHeader,
  WizardTitle,
  WizardProgress,
  ProgressBar,
  ProgressStep,
  WizardContent,
  WizardNavigation,
  BackButton,
  NextButton,
  SkipButton,
  CompleteButton,
  StepIndicator
} from '../../../styles/Profile/ProfileSetup.styles';

interface ProfileSetupWizardProps {
  onComplete: (data: ProfileSetupData) => void;
  onCancel: () => void;
  initialData?: Partial<ProfileSetupData>;
}

const STEPS = [
  { id: 1, title: 'Basic Info', component: BasicInfoStep },
  { id: 2, title: 'Photos', component: PhotosStep },
  { id: 3, title: 'Bio', component: BioStep },
  { id: 4, title: 'Interests', component: InterestsStep },
  { id: 5, title: 'Preferences', component: PreferencesStep }
];

export const ProfileSetupWizard: React.FC<ProfileSetupWizardProps> = ({
  onComplete,
  onCancel,
  initialData
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [profileData, setProfileData] = useState<ProfileSetupData>({
    basicInfo: {
      firstName: '',
      lastName: '',
      dateOfBirth: new Date(),
      gender: 'other',
      location: {
        city: '',
        state: '',
        country: ''
      },
      occupation: '',
      education: ''
    },
    photos: [],
    bio: {
      content: '',
      characterCount: 0,
      maxCharacters: 500,
      lastUpdated: new Date()
    },
    interests: [],
    preferences: {
      ageRange: { min: 18, max: 65 },
      distance: 25,
      genderPreference: [],
      interestedIn: [],
      dealBreakers: []
    },
    currentStep: 1,
    isComplete: false,
    verificationStatus: 'pending',
    ...initialData
  });

  const [stepData, setStepData] = useState<Record<string, any>>({});
  const [isStepValid, setIsStepValid] = useState<Record<number, boolean>>({});

  const currentStepInfo = STEPS.find(step => step.id === currentStep);
  const CurrentStepComponent = currentStepInfo?.component;

  const handleStepDataChange = (stepId: number, data: any, isValid: boolean) => {
    setStepData(prev => ({ ...prev, [stepId]: data }));
    setIsStepValid(prev => ({ ...prev, [stepId]: isValid }));
  };

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep(prev => prev + 1);
      setProfileData(prev => ({
        ...prev,
        currentStep: currentStep + 1
      }));
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      setProfileData(prev => ({
        ...prev,
        currentStep: currentStep - 1
      }));
    }
  };

  const handleSkip = () => {
    if (currentStep < STEPS.length) {
      handleNext();
    }
  };

  const handleComplete = () => {
    const completedData: ProfileSetupData = {
      ...profileData,
      isComplete: true,
      currentStep: STEPS.length
    };
    onComplete(completedData);
  };

  const canProceed = isStepValid[currentStep] || false;
  const isLastStep = currentStep === STEPS.length;
  const progressPercentage = (currentStep / STEPS.length) * 100;

  return (
    <WizardContainer>
      <WizardHeader>
        <WizardTitle>Create Your Profile</WizardTitle>
        <WizardProgress>
          <ProgressBar>
            <div 
              style={{ 
                width: `${progressPercentage}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '10px',
                transition: 'width 0.3s ease'
              }}
            />
          </ProgressBar>
          <StepIndicator>
            Step {currentStep} of {STEPS.length}
          </StepIndicator>
        </WizardProgress>
      </WizardHeader>

      <WizardContent>
        {CurrentStepComponent && (
          <CurrentStepComponent
            data={stepData[currentStep] || {}}
            onChange={(data, isValid) => handleStepDataChange(currentStep, data, isValid)}
            onNext={handleNext}
            onBack={handleBack}
            isFirstStep={currentStep === 1}
            isLastStep={isLastStep}
          />
        )}
      </WizardContent>

      <WizardNavigation>
        <div>
          {currentStep > 1 && (
            <BackButton onClick={handleBack}>
              ← Back
            </BackButton>
          )}
        </div>

        <div>
          {!isLastStep ? (
            <>
              <SkipButton onClick={handleSkip}>
                Skip for now
              </SkipButton>
              <NextButton 
                onClick={handleNext}
                disabled={!canProceed}
              >
                Next →
              </NextButton>
            </>
          ) : (
            <CompleteButton 
              onClick={handleComplete}
              disabled={!canProceed}
            >
              Complete Setup
            </CompleteButton>
          )}
        </div>
      </WizardNavigation>
    </WizardContainer>
  );
};
